/**
 * Human-readable Czech labels for audit log action and entity type values.
 *
 * Keep in sync with internal/auth/actions.go.
 * When adding a new Action or EntityType constant there, add the label here.
 * ENTITY_TYPE_OPTIONS is derived automatically — no manual update needed.
 */

export const ACTION_LABELS: Record<string, string> = {
  // Auth
  "auth.login.success": "Přihlášení",
  "auth.login.failure": "Neúspěšné přihlášení",
  "auth.logout": "Odhlášení",
  "auth.password_reset.requested": "Žádost o obnovu hesla",

  // User
  "user.created": "Vytvoření uživatele",
  "user.updated": "Úprava uživatele",
  "user.password.changed": "Změna hesla",
  "user.password.reset": "Obnova hesla",

  // Team
  "team.registered": "Registrace týmu",
  "team.created": "Založení týmu",
  "team.approved": "Schválení týmu",
  "team.suspended": "Pozastavení týmu",
  "team.reactivated": "Aktivace týmu",
  "team.renamed": "Přejmenování týmu",
  "team.federation.changed": "Přesun týmu do jiné federace",
  "federation.created": "Založení federace",
  "federation.updated": "Úprava federace",
  "federation.deleted": "Smazání federace",

  // Team leadership
  "team.leadership.assigned": "Přiřazení vedoucího",
  "team.leadership.ended": "Ukončení vedení týmu",
  "team.leadership.transferred": "Předání vedení týmu",
  "team.leader.login_created": "Založení účtu vedoucímu",

  // Person / membership
  "person.updated": "Úprava osoby",
  "person.photo.uploaded": "Nahrání fotky",
  "person.photo.removed": "Odebrání fotky",
  "person.distinct.confirmed": "Označení osob jako různých",
  "person.distinct.revoked": "Zrušení označení osob jako různých",
  "membership.created": "Přidání člena",
  "membership.updated": "Úprava členství",
  "membership.registered": "Registrace do sezóny",
  "membership.unregistered": "Zrušení registrace do sezóny",
  "membership.removed": "Odebrání člena",
  "membership.roster.cloned": "Převod soupisky",
  "season_registration.submitted": "Odeslání registrace do sezóny",
  "season_registration.draft.created": "Založení rozpracované registrace",
  "season_registration.draft.discarded": "Zahození rozpracované registrace",
  "season_registration.draft.purged": "Úklid rozpracovaných registrací",
  // Tým vstupuje do sezóny a vystupuje z ní. Na rozdíl od řádků výš, které
  // mluví o jednom člověku na soupisce, tyhle dva říkají, jestli je v sezóně
  // samotný tým: na tom visí členský příspěvek i přihlášky na MČR.
  "team.season.registered": "Registrace týmu do sezóny",
  "team.season.unregistered": "Zrušení registrace týmu do sezóny",
  "team.documents.agreed": "Souhlas s dokumenty sezóny",
  "team.documents.consent_revoked": "Odebrání souhlasu s dokumentem",

  // Role assignments (stored RBAC roles only).
  "role.assigned": "Přidělení role",
  "role.revoked": "Odebrání role",

  // ČNSMS ID allocation
  "cmfid.team.allocated": "Přidělení ČNSMS ID týmu",
  "cmfid.person.allocated": "Přidělení ČNSMS ID osobě",

  // Billing info
  "billing_info.updated": "Úprava fakturačních údajů",

  // Team payment
  "team.payment.updated": "Úprava platby týmu",

  // Import snapshotu (cmfis import)
  "snapshot.imported": "Import dat",

  // Season
  "season.created": "Vytvoření sezóny",
  "season.updated": "Úprava sezóny",
  "season.activated": "Aktivace sezóny",
  "season.document.created": "Přidání dokumentu sezóny",
  "season.document.file_replaced": "Nová verze dokumentu sezóny",
  "season.document.renamed": "Přejmenování dokumentu sezóny",
  "season.document.reordered": "Změna pořadí dokumentů sezóny",
  "season.document.retired": "Vyřazení dokumentu sezóny",

  // Impersonation
  "impersonation.started": "Zahájení vystupování za jiného uživatele",
  "impersonation.stopped": "Ukončení vystupování za jiného uživatele",

  // Sady pravidel
  "ruleset.created": "Vytvoření sady pravidel",
  "ruleset.updated": "Úprava sady pravidel",
  "ruleset.deleted": "Smazání sady pravidel",
  "ruleset.duplicated": "Duplikace sady pravidel",
  "ruleset.status.changed": "Změna stavu sady pravidel",

  // Disciplíny
  "discipline.created": "Vytvoření disciplíny",
  "discipline.updated": "Úprava disciplíny",
  "discipline.deleted": "Smazání disciplíny",
  "discipline.categories.updated": "Úprava kombinací disciplíny",

  // Součásti sady pravidel
  "rules.component.created": "Vytvoření součásti pravidel",
  "rules.component.updated": "Úprava součásti pravidel",
  "rules.component.deleted": "Smazání součásti pravidel",

  // Soutěže
  "competition.created": "Vytvoření soutěže",
  "competition.updated": "Úprava soutěže",
  "competition.deleted": "Smazání soutěže",
  "competition.status.changed": "Změna stavu soutěže",
  "competition.panel.created": "Vytvoření poroty",
  "competition.panel.renamed": "Přejmenování poroty",
  "competition.panel.deleted": "Smazání poroty",
  "competition.panels.prepared": "Příprava porot",
  "competition.panel.block_assigned": "Přidělení poroty",
  "competition.panel.seat_assigned": "Obsazení místa v porotě",
  "competition.classes.updated": "Úprava vypsaných výkonnostních tříd",
  "competition.series.created": "Vytvoření série",
  "competition.offer.updated": "Úprava vypsaných disciplín a výkonnostních tříd",

  // Přihlášky
  "competition_entry.created": "Podání přihlášky",
  "competition_entry.updated": "Úprava přihlášky",
  "competition_entry.deleted": "Smazání přihlášky",
  "competition_entry.withdrawn": "Odhláška",
  "competition_entry.reinstated": "Vrácení odhlášky",
  "competition_category.generated": "Doplnění soutěžních kategorií",
  "competition_category.merged": "Sloučení soutěžních kategorií",
  "competition_category.split": "Rozdělení soutěžní kategorie",
  "startlist.built": "Aktualizace startovní listiny",
  "startlist.updated": "Úprava startovní listiny",
  "startlist.items.updated": "Úprava položek startovní listiny",
  "startlist.draft_published": "Zveřejnění návrhu startovní listiny",
  "startlist.published": "Zveřejnění startovní listiny",
  "startlist.unpublished": "Zrušení zveřejnění startovní listiny",
  "mail.sent_now": "Okamžité odeslání e-mailu",
  "mail.cancelled": "Zrušení e-mailu",
  "mail.settings_updated": "Změna nastavení e-mailů",
  "access.updated": "Změna nastavení přístupu",
  "notice.created": "Vytvoření oznámení",
  "notice.updated": "Úprava oznámení",
  "notice.deleted": "Smazání oznámení",
  "custom_page.created": "Vytvoření stránky",
  "custom_page.updated": "Úprava stránky",
  "custom_page.deleted": "Smazání stránky",
  "custom_page.viewed": "Zobrazení stránky",

  // Hudba (Modul 6). „Nahrání" and „Výměna" are kept apart because
  // music is replace-only: replacing is the everyday act, and an outright
  // removal is the rare admin purge that should stand out in the log.
  "entry_music.uploaded": "Nahrání hudby k přihlášce",
  "entry_music.replaced": "Výměna hudby u přihlášky",
  "entry_music.removed": "Smazání hudby u přihlášky",
  "recording.file.replaced": "Výměna souboru nahrávky ČNSMS",
  // Historic: emitted before nahrávky ČNSMS became řádky sady pravidel.
  // Nothing writes them; the labels stay so old entries still read.
  "discipline_music.uploaded": "Nahrání hudby ČNSMS",
  "discipline_music.replaced": "Výměna hudby ČNSMS",
  "discipline_music.removed": "Smazání hudby ČNSMS",
};

export const ENTITY_TYPE_LABELS: Record<string, string> = {
  user: "Uživatel",
  session: "Relace",
  team: "Tým",
  team_leadership: "Vedení týmu",
  billing_info: "Fakturační údaje",
  person: "Osoba",
  person_distinct_pair: "Různé osoby",
  membership: "Členství",
  role_assignment: "Přiřazení role",
  season: "Sezóna",
  season_document: "Dokument sezóny",
  federation: "Federace",
  ruleset: "Sada pravidel",
  discipline: "Disciplína",
  age_category_table: "Tabulka věkových kategorií",
  formation_table: "Tabulka formací",
  performance_class_table: "Tabulka výkonnostních tříd",
  deduction_table: "Srážková tabulka",
  scoring_sheet: "Hodnoticí tabulka",
  floor: "Soutěžní plocha",
  recording: "Nahrávka ČNSMS",
  discipline_family: "Skupina disciplín",
  competition: "Soutěž",
  competition_series: "Série",
  competition_entry: "Přihláška",
  competition_category: "Soutěžní kategorie",
  mail: "E-mail",
  mail_settings: "Nastavení e-mailů",
  access_settings: "Nastavení přístupu",
  notice: "Oznámení",
  custom_page: "Stránka",
};

/** Options for the entity-type filter <Select>. Derived automatically. */
export const ENTITY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Všechny" },
  ...Object.entries(ENTITY_TYPE_LABELS)
    .sort((a, b) => a[1].localeCompare(b[1], "cs"))
    .map(([value, label]) => ({ value, label })),
];

export function labelAction(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function labelEntityType(entityType: string): string {
  return ENTITY_TYPE_LABELS[entityType] ?? entityType;
}
